import {Component, Inject, Input} from '@angular/core';
import {UntypedFormArray} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';

import {filePath, isErrored, SourceBibApiService, SwalService} from '@atmi/core';
import {isSourceBibliographic, SourceBib, SourceBibCard, SourceBibMeta} from '@atmi/core/models';
import {SourceBibDialogComponent} from '@atmi/shared/source-bib-dialog/source-bib-dialog.component';
import {TranslateService} from '@ngx-translate/core';

import {BaseCtrlValueAccessorFormArrayComponent, FormManagerService} from '@sim/form';
import {ClassName} from '@sim/role';
import {NgxSpinnerService} from 'ngx-spinner';

import {BehaviorSubject, combineLatest, firstValueFrom, forkJoin} from 'rxjs';
import {catchError, filter, first, map, switchMap, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-source-bib-manager',
  styleUrls: ['./source-bib-manager.component.scss'],
  templateUrl: './source-bib-manager.component.html',
})
export class SourceBibManagerComponent extends BaseCtrlValueAccessorFormArrayComponent<SourceBib> {

  @Input() classNames: ClassName[] = [];
  @Input() spinner!: NgxSpinnerService;

  private readonly cardId$ = new BehaviorSubject<string | undefined>(undefined);

  @Input() set cardId(v: string | undefined) {
    this.cardId$.next(v);
  }

  get cardId() {
    return this.cardId$.value;
  }

  readonly cardSourceBibs$ = combineLatest([
    this.cardId$.pipe(),
    this.formValue$.pipe(filter((form): form is UntypedFormArray => !!form)),
  ]).pipe(
    switchMap(([_, form]) => {
      const sourceBibsIds: string[] = form.value.map((s: SourceBib) => s.docBib);

      const sourceBibsPromises = this.sourceBibApi.getManyWithFile(sourceBibsIds);

      return sourceBibsPromises.length > 0
        ? forkJoin(sourceBibsPromises)
        : Promise.resolve([]);
    }),
    map((sourcesBibliographicResponse) => {
      if (!this.form) {
        throw new Error('Cannot set bibliographic list without a form');
      }
      const errored = sourcesBibliographicResponse.filter(isErrored);
      const sourceBibliographics = sourcesBibliographicResponse.filter(isSourceBibliographic);
      const sourcesBibs: SourceBib[] = this.form.value;
      this.spinner?.hide();

      if (errored.length > 0) {
        const html = errored.map((e: any) => `${e.id}:${e.error?.error?.error || ''}`).join('<br>');
        this.swal.showError({title: `Errore durante il caricamento di ${errored.length} su ${sourcesBibliographicResponse.length}`, html});
      }

      return sourcesBibs.map((source) => {
        const linked = sourceBibliographics.find((s) => s.id === source.docBib);

        return {
          ...source,
          docBib: {...linked},
          ordine: source.ordine,
          riferimento: source.riferimento,
        };
      });
    }),
    catchError((err: any) => {
      console.error(err);
      this.spinner?.hide();
      throw new Error('Error fetching source bibliographic');
    }),
    takeUntil(this.unsubscription$),
  );

  constructor(
    private readonly sourceBibApi: SourceBibApiService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly swal: SwalService,
    private readonly formManager: FormManagerService,
    @Inject(filePath) readonly fileUrl: string,
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    /*@TODO pass spinner in html. nowhere is passed to this component*/
    this.spinner?.show();
  }

  orderSourceBinHandler(items: Array<SourceBibCard & { docBib: { id: string } }>) {
    this.value = items.map((i, index) => ({
      docBib: i.docBib.id,
      ordine: index + 1,
      riferimento: i.riferimento,
    }));
  }

  async addSourceBibHandler(lastSourceBib?: SourceBibCard) {
    const ref = this.dialog.open(SourceBibDialogComponent, {
      data: {order: lastSourceBib ? lastSourceBib.ordine : 1},
      width: '1000px'
    });
    const res: SourceBib | undefined = await firstValueFrom(ref.afterClosed().pipe(first()));

    if (res && this.form) {
      const group = this.formManager.makeForm(SourceBibMeta, res);

      this.form.push(group);
      this.value = [...this.form.value];
    }
  }

  // tslint:disable-next-line:variable-name
  async removeSourceBibHandler(_: SourceBibCard, index: number) {
    const res = await this.swal.showConfirm({
      text: this.translate.instant('Sei sicuro di voler rimuovere questa fonte?'),
      title: this.translate.instant('Rimozione fonte bibliografica'),
    });

    if (res && this.form) {
      this.form.removeAt(index);
      this.value = [...this.form.value];
    }
  }
}
